"use client"

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react"

const STORAGE_KEY = "ukss_wishlist"

type WishlistContextType = {
  items: string[]
  add: (productId: string) => void
  remove: (productId: string) => void
  toggle: (productId: string) => void
  isInWishlist: (productId: string) => boolean
  count: number
}

const WishlistContext = createContext<WishlistContextType>({
  items: [],
  add: () => {},
  remove: () => {},
  toggle: () => {},
  isInWishlist: () => false,
  count: 0,
})

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setItems(JSON.parse(stored))
      }
    } catch {}
    setLoaded(true)
  }, [])

  // Persist to localStorage on change
  useEffect(() => {
    if (!loaded) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {}
  }, [items, loaded])

  const add = useCallback((productId: string) => {
    setItems((prev) => (prev.includes(productId) ? prev : [...prev, productId]))
  }, [])

  const remove = useCallback((productId: string) => {
    setItems((prev) => prev.filter((id) => id !== productId))
  }, [])

  const toggle = useCallback((productId: string) => {
    setItems((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    )
  }, [])

  const isInWishlist = useCallback(
    (productId: string) => items.includes(productId),
    [items]
  )

  const value = useMemo(
    () => ({ items, add, remove, toggle, isInWishlist, count: items.length }),
    [items, add, remove, toggle, isInWishlist]
  )

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  )
}

export const useWishlist = () => useContext(WishlistContext)
